import { BLOCKS, type BlockId, type BlueprintLevel, type Voxel } from './blueprint.ts';

/**
 * Blueprints are drawn as text, one block of rows per layer from the ground up. In each layer the
 * rows run north → south (z) and the characters west → east (x), exactly as in the layer editor.
 *   .  air   S stone   C cobble   P planks   L log    B brick    G glass
 *   F leaves A sand    T slate    N lantern  R red wool           U blue wool
 * These are generic structures made up for the game, not builds from the family's world.
 */
const LEGEND: Record<string, BlockId> = {
  S: 'stone',
  C: 'cobble',
  P: 'planks',
  L: 'log',
  B: 'brick',
  G: 'glass',
  F: 'leaves',
  A: 'sand',
  T: 'slate',
  N: 'lantern',
  R: 'wool-red',
  U: 'wool-blue',
};

function model(layers: string[][]): Voxel[] {
  const out: Voxel[] = [];
  layers.forEach((rows, y) =>
    rows.forEach((row, z) =>
      [...row].forEach((ch, x) => {
        if (ch === '.') return;
        const b = LEGEND[ch];
        if (!b) throw new Error(`unknown blueprint character ${ch}`);
        out.push([x, y, z, b]);
      }),
    ),
  );
  return out;
}

/** The blocks the model uses plus a decoy or two, in catalogue order. */
const paletteOf = (m: Voxel[], decoys: BlockId[] = []) =>
  BLOCKS.filter((b) => decoys.includes(b) || m.some((v) => v[3] === b));

type Def = Omit<BlueprintLevel, 'palette' | 'model' | 'version'> & {
  layers: string[][];
  decoys?: BlockId[];
};
const level = ({ layers, decoys, ...d }: Def): BlueprintLevel => {
  const m = model(layers);
  return { ...d, version: 1, model: m, palette: paletteOf(m, decoys) };
};

const ring4 = (c: string) => [
  '........',
  '........',
  `..${c}${c}${c}${c}..`,
  `..${c}..${c}..`,
  `..${c}..${c}..`,
  `..${c}${c}${c}${c}..`,
];
const full4 = (c: string) => [
  '........',
  '........',
  `..${c.repeat(4)}..`,
  `..${c.repeat(4)}..`,
  `..${c.repeat(4)}..`,
  `..${c.repeat(4)}..`,
];

export const BLUEPRINT_LEVELS: BlueprintLevel[] = [
  // ---------- tier 1: 4×4×4 ----------
  level({
    id: 'lantern-post',
    tier: 1,
    title: 'Lantern post',
    goal: 'A stone footing, two logs and a lantern on top.',
    size: 4,
    rotations: false,
    layers: [
      ['....', '.S..'],
      ['....', '.L..'],
      ['....', '.L..'],
      ['....', '.N..'],
    ],
  }),
  level({
    id: 'tiny-shelter',
    tier: 1,
    title: 'Tiny shelter',
    goal: 'Four log posts holding up a plank roof.',
    size: 4,
    rotations: true,
    decoys: ['cobble'],
    layers: [
      ['L.L.', '....', 'L.L.'],
      ['L.L.', '....', 'L.L.'],
      ['PPP.', 'PPP.', 'PPP.'],
    ],
  }),
  level({
    id: 'sapling',
    tier: 1,
    title: 'Sapling',
    goal: 'A little tree: a short trunk with a cross of leaves.',
    size: 4,
    rotations: true,
    layers: [
      ['....', '.L..'],
      ['....', '.L..'],
      ['.F..', 'FLF.', '.F..'],
      ['....', '.F..'],
    ],
  }),
  level({
    id: 'steps',
    tier: 1,
    title: 'Steps',
    goal: 'Cobble steps climbing to a lantern.',
    size: 4,
    rotations: true,
    decoys: ['stone'],
    layers: [['CCCC', 'CCCC'], ['.CCC', '.CCC'], ['..CC', '..CC'], ['...N']],
  }),

  // ---------- tier 2: 6×6×6 ----------
  level({
    id: 'plank-bridge',
    tier: 2,
    title: 'Plank bridge',
    goal: 'Stone piers, a plank deck, log posts and lanterns.',
    size: 6,
    rotations: true,
    decoys: ['cobble'],
    layers: [
      ['......', '......', 'SS..SS', 'SS..SS'],
      ['......', '......', 'PPPPPP', 'PPPPPP'],
      ['......', '......', 'L....L', 'L....L'],
      ['......', '......', 'N....N'],
    ],
  }),
  level({
    id: 'village-well',
    tier: 2,
    title: 'Village well',
    goal: 'A cobble rim around water, posts, and a slate roof.',
    size: 6,
    rotations: false,
    decoys: ['stone'],
    layers: [
      ['......', '.CCCC.', '.CUUC.', '.CUUC.', '.CCCC.'],
      ['......', '.L..L.', '......', '......', '.L..L.'],
      ['......', '.L..L.', '......', '......', '.L..L.'],
      ['......', '.TTTT.', '.TTTT.', '.TTTT.', '.TTTT.'],
      ['......', '......', '..TT..', '..TT..'],
    ],
  }),
  level({
    id: 'market-stall',
    tier: 2,
    title: 'Market stall',
    goal: 'A plank counter under a striped awning.',
    size: 6,
    rotations: true,
    layers: [
      ['......', '.L..L.', '.PPPP.', '......', '.L..L.'],
      ['......', '.L..L.', '......', '......', '.L..L.'],
      ['......', '.RURU.', '.RURU.', '.RURU.', '.RURU.'],
    ],
  }),
  level({
    id: 'lookout',
    tier: 2,
    title: 'Lookout',
    goal: 'A thick log post with a plank platform on top.',
    size: 6,
    rotations: true,
    decoys: ['planks'],
    layers: [
      ['......', '......', '..LL..', '..LL..'],
      ['......', '......', '..LL..', '..LL..'],
      ['......', '......', '..LL..', '..LL..'],
      ['......', '.PPPP.', '.PPPP.', '.PPPP.'],
      ['......', '.N..N.'],
    ],
  }),

  // ---------- tier 3: 8×8×8 ----------
  level({
    id: 'stone-tower',
    tier: 3,
    title: 'Stone tower',
    goal: 'A hollow tower with a door on the south side, windows and battlements.',
    size: 8,
    rotations: false,
    decoys: ['brick'],
    layers: [
      ['........', '........', '..SSSS..', '..S..S..', '..S..S..', '..SS.S..'],
      ['........', '........', '..SSSS..', '..S..S..', '..S..S..', '..SS.S..'],
      ['........', '........', '..SSSS..', '..G..G..', '..S..S..', '..SSSS..'],
      ring4('S'),
      full4('C'),
      ['........', '........', '..C..C..', '........', '........', '..C..C..'],
    ],
  }),
  level({
    id: 'cottage',
    tier: 3,
    title: 'Cottage',
    goal: 'Brick walls, two back windows, a door and a slate roof.',
    size: 8,
    rotations: true,
    decoys: ['stone', 'planks'],
    layers: [
      ['........', '.BBBBB..', '.B...B..', '.B...B..', '.BB.BB..', '.....N..'],
      ['........', '.BGBGB..', '.B...B..', '.B...B..', '.BB.BB..'],
      ['........', '.TTTTT..', '.TTTTT..', '.TTTTT..', '.TTTTT..'],
      ['........', '........', '.TTTTT..', '.TTTTT..'],
    ],
  }),
  level({
    id: 'big-oak',
    tier: 3,
    title: 'Big oak',
    goal: 'A tall trunk, a wide crown of leaves and a sandy path.',
    size: 8,
    rotations: true,
    layers: [
      ['........', '........', '........', '...L....', '........', '..AAA...'],
      ['........', '........', '........', '...L....'],
      ['........', '........', '..FFF...', '..FLF...', '..FFF...'],
      ['........', '...F....', '.FFFFF..', '.FFLFF..', '.FFFFF..', '...F....'],
      ['........', '........', '..FFF...', '..FFF...', '...F....'],
      ['........', '........', '........', '...F....'],
    ],
  }),
  level({
    id: 'lighthouse',
    tier: 3,
    title: 'Lighthouse',
    goal: 'Striped walls, a glass lamp room with lanterns, and a slate cap.',
    size: 8,
    rotations: false,
    decoys: ['brick'],
    layers: [
      full4('C'),
      ring4('R'),
      ring4('S'),
      ring4('R'),
      ['........', '........', '..GGGG..', '..GNNG..', '..GNNG..', '..GGGG..'],
      full4('T'),
      ['........', '........', '........', '...TT...', '...TT...'],
    ],
  }),
];
